import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ConfirmImportModal,
  CsvDropzone,
  ImportProgress,
  LeavePageModal,
  PayoutRowsTable,
  SelectedFileCard,
  buildProgressLabel,
} from '../components/shopperPayout';
import { ROUTES } from '../constants';
import shopperPayoutService, {
  CANCELABLE_STATUSES,
  IMPORT_STATUS,
  IN_PROGRESS_STATUSES,
  OPEN_STATUSES,
  saveTextAsFile,
} from '../services/shopperPayoutService';

const POLL_INTERVAL_MS = 1500;

/**
 * The id of the batch being worked on, so a page reload lands back on the same
 * step instead of losing it. A validation of 10,000 rows outlives things that
 * reload the app on their own (the auth context reloads after a token refresh).
 */
const ACTIVE_BATCH_KEY = 'skai1_shopper_payout_active_import';

const readActiveBatchId = () => {
  try {
    return sessionStorage.getItem(ACTIVE_BATCH_KEY);
  } catch (error) {
    return null;
  }
};

const writeActiveBatchId = id => {
  try {
    if (id) sessionStorage.setItem(ACTIVE_BATCH_KEY, id);
    else sessionStorage.removeItem(ACTIVE_BATCH_KEY);
  } catch (error) {
    /* private mode / storage disabled — resuming is a nice-to-have */
  }
};

const TERMINAL_STATUSES = [
  IMPORT_STATUS.COMPLETED,
  IMPORT_STATUS.COMPLETED_WITH_ERRORS,
  IMPORT_STATUS.FAILED_INCOMPLETE,
];

/**
 * Bulk Import Shopper Payout wizard.
 *
 * One route drives the whole flow; the step is derived from the backend status
 * of the current batch:
 *   (no batch)  -> upload
 *   pending     -> file accepted, ready to validate
 *   validating  -> progress, polled
 *   validated   -> validation result + confirm
 *   processing  -> progress, polled
 *   completed*  -> redirect to the import detail page
 */
const ShopperPayoutImportPage = () => {
  const navigate = useNavigate();

  const [batch, setBatch] = useState(null);
  const [fileError, setFileError] = useState(null);
  const [error, setError] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [startingValidation, setStartingValidation] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showLeaveModal, setShowLeaveModal] = useState(false);

  // Kept in refs so the unmount cleanup and the poller always see fresh values
  // without re-subscribing on every render.
  const batchRef = useRef(null);
  const finalizedRef = useRef(false);
  const shouldOpenPickerRef = useRef(false);
  const hasResumedRef = useRef(false);

  const status = batch?.status || null;
  const isPolling = IN_PROGRESS_STATUSES.includes(status);
  const isProcessing = status === IMPORT_STATUS.PROCESSING;
  const isValidationResult = status === IMPORT_STATUS.VALIDATED;
  const hasOpenBatch = Boolean(batch?.id) && OPEN_STATUSES.includes(status);
  // Once the batch is `processing` the payouts are already being executed, so
  // it can no longer be canceled — only followed.
  const canCancelBatch =
    Boolean(batch?.id) && CANCELABLE_STATUSES.includes(status);
  const busy =
    uploading || startingValidation || confirming || leaving || resetting;

  useEffect(() => {
    batchRef.current = batch;
  }, [batch]);

  // Pick the batch back up after a reload (see ACTIVE_BATCH_KEY).
  useEffect(() => {
    if (hasResumedRef.current) return;
    hasResumedRef.current = true;

    const storedId = readActiveBatchId();
    if (!storedId) return;

    let cancelled = false;
    shopperPayoutService
      .getImport(storedId)
      .then(resumed => {
        if (cancelled) return;
        if (OPEN_STATUSES.includes(resumed.status)) setBatch(resumed);
        else writeActiveBatchId(null);
      })
      .catch(() => writeActiveBatchId(null));

    return () => {
      cancelled = true;
    };
  }, []);

  // Keep the stored id in step with the batch actually being worked on.
  useEffect(() => {
    if (batch?.id && OPEN_STATUSES.includes(batch.status)) {
      writeActiveBatchId(batch.id);
    } else {
      writeActiveBatchId(null);
    }
  }, [batch?.id, batch?.status]);

  // Poll while the backend is validating or processing.
  useEffect(() => {
    if (!batch?.id || !isPolling) return undefined;

    let cancelled = false;
    let inFlight = false;
    const batchId = batch.id;

    const poll = async () => {
      // A slow response must not stack up behind the next tick.
      if (inFlight) return;
      inFlight = true;
      try {
        const next = await shopperPayoutService.getImport(batchId);
        if (!cancelled) setBatch(next);
      } catch (err) {
        if (!cancelled) {
          setError(
            shopperPayoutService.getErrorMessage(
              err,
              'Lost connection to the import. Please try again.'
            )
          );
        }
      } finally {
        inFlight = false;
      }
    };

    const intervalId = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [batch?.id, isPolling]);

  // Once the payouts are processed there is nothing left to do here — the
  // outcome lives on the import detail page.
  useEffect(() => {
    if (!batch?.id || !TERMINAL_STATUSES.includes(batch.status)) return;

    finalizedRef.current = true;
    navigate(
      ROUTES.generatePath(ROUTES.SHOPPER_PAYOUT_DETAIL, { id: batch.id }),
      { replace: true }
    );
  }, [batch?.id, batch?.status, navigate]);

  // Warn on tab close / refresh while a batch is still open.
  useEffect(() => {
    if (!hasOpenBatch) return undefined;

    const handleBeforeUnload = event => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasOpenBatch]);

  // Leaving the page (sidebar, browser back, ...) cancels the open batch so it
  // does not sit half-finished on the backend.
  useEffect(
    () => () => {
      const current = batchRef.current;
      if (
        !finalizedRef.current &&
        current?.id &&
        CANCELABLE_STATUSES.includes(current.status)
      ) {
        shopperPayoutService.cancelImportSilently(current.id);
      }
      writeActiveBatchId(null);
    },
    []
  );

  const handleRowsError = useCallback(message => setError(message), []);

  const handleFileSelected = useCallback(async file => {
    setFileError(null);
    setError(null);

    const rejection = shopperPayoutService.getFileRejectionMessage(file);
    if (rejection) {
      setFileError(rejection);
      return;
    }

    setUploading(true);
    try {
      const created = await shopperPayoutService.uploadFile(file);
      finalizedRef.current = false;
      setBatch(created);
    } catch (err) {
      setFileError(
        shopperPayoutService.getErrorMessage(
          err,
          'Failed to upload the CSV file.'
        )
      );
    } finally {
      setUploading(false);
    }
  }, []);

  const handleStartValidation = useCallback(async () => {
    if (!batch?.id) return;

    setStartingValidation(true);
    setError(null);
    try {
      const next = await shopperPayoutService.startValidation(batch.id);
      setBatch(next);
    } catch (err) {
      setError(
        shopperPayoutService.getErrorMessage(err, 'Failed to start validation.')
      );
    } finally {
      setStartingValidation(false);
    }
  }, [batch?.id]);

  /**
   * Drop the current batch on the backend and go back to the empty dropzone.
   * @param {boolean} openPicker - Reopen the file dialog straight away.
   */
  const resetBatch = useCallback(async (openPicker = false) => {
    const current = batchRef.current;

    if (current?.id && CANCELABLE_STATUSES.includes(current.status)) {
      setResetting(true);
      try {
        await shopperPayoutService.cancelImport(current.id);
      } catch (err) {
        setError(
          shopperPayoutService.getErrorMessage(
            err,
            'Failed to cancel the current import.'
          )
        );
        return;
      } finally {
        setResetting(false);
      }
    }

    finalizedRef.current = false;
    setBatch(null);
    setFileError(null);
    shouldOpenPickerRef.current = openPicker;
  }, []);

  // Reopen the file dialog after "Replace File" once the dropzone is back.
  useEffect(() => {
    if (batch || !shouldOpenPickerRef.current) return;
    shouldOpenPickerRef.current = false;
    document.querySelector('[data-testid="csv-file-input"]')?.click();
  }, [batch]);

  const handleConfirmImport = useCallback(async () => {
    if (!batch?.id) return;

    setConfirming(true);
    setError(null);
    try {
      const next = await shopperPayoutService.confirmImport(batch.id);
      setShowConfirmModal(false);
      setBatch(next);
    } catch (err) {
      setShowConfirmModal(false);
      setError(
        shopperPayoutService.getErrorMessage(
          err,
          'Failed to confirm the import.'
        )
      );
      // The state guard rejected us — resync so the UI shows reality.
      try {
        setBatch(await shopperPayoutService.getImport(batch.id));
      } catch (refreshError) {
        // keep the message above; nothing else we can do here
      }
    } finally {
      setConfirming(false);
    }
  }, [batch?.id]);

  const handleBack = useCallback(() => {
    // Payouts are already running: leaving is safe and stops nothing, so send
    // the admin to the detail page where the same batch keeps updating.
    if (isProcessing && batch?.id) {
      finalizedRef.current = true;
      navigate(
        ROUTES.generatePath(ROUTES.SHOPPER_PAYOUT_DETAIL, { id: batch.id })
      );
      return;
    }

    if (canCancelBatch) {
      setShowLeaveModal(true);
      return;
    }

    navigate(ROUTES.SHOPPER_PAYOUT);
  }, [isProcessing, canCancelBatch, batch?.id, navigate]);

  const handleLeavePage = useCallback(async () => {
    setLeaving(true);
    const current = batchRef.current;

    if (current?.id && CANCELABLE_STATUSES.includes(current.status)) {
      try {
        await shopperPayoutService.cancelImport(current.id);
      } catch (err) {
        // The batch may have advanced in the meantime — leaving anyway.
      }
    }

    finalizedRef.current = true;
    setLeaving(false);
    setShowLeaveModal(false);
    navigate(ROUTES.SHOPPER_PAYOUT);
  }, [navigate]);

  const handleDownloadTemplate = useCallback(async () => {
    setDownloadingTemplate(true);
    try {
      const { content, fileName } = await shopperPayoutService.getTemplate();
      saveTextAsFile(content, fileName);
    } catch (err) {
      setError(
        shopperPayoutService.getErrorMessage(
          err,
          'Failed to download the CSV template.'
        )
      );
    } finally {
      setDownloadingTemplate(false);
    }
  }, []);

  const renderErrorBanner = () =>
    error && (
      <div className='bg-red-50 border border-red-200 rounded-lg p-4 mb-6 flex items-start gap-2'>
        <svg
          className='w-5 h-5 text-red-400 flex-shrink-0 mt-0.5'
          fill='none'
          stroke='currentColor'
          viewBox='0 0 24 24'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z'
          />
        </svg>
        <p className='text-sm text-red-700 whitespace-pre-line flex-1'>
          {error}
        </p>
        <button
          type='button'
          onClick={() => setError(null)}
          aria-label='Dismiss error'
          className='text-red-400 hover:text-red-600'
        >
          <svg
            className='w-4 h-4'
            fill='none'
            stroke='currentColor'
            viewBox='0 0 24 24'
          >
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M6 18L18 6M6 6l12 12'
            />
          </svg>
        </button>
      </div>
    );

  const renderUploadStep = () => (
    <div className='bg-white rounded-lg shadow-sm border border-gray-200 p-6 md:p-8'>
      <h2 className='text-2xl font-bold text-gray-900'>
        Bulk Import Shopper Payout
      </h2>
      <p className='mt-1 text-sm text-gray-600'>
        Upload multiple shopper payouts using a CSV file.
      </p>

      <div className='mt-6'>
        {batch ? (
          <SelectedFileCard
            batch={batch}
            onReplace={() => resetBatch(true)}
            onRemove={() => resetBatch(false)}
            disabled={busy}
          />
        ) : (
          <CsvDropzone
            onFileSelected={handleFileSelected}
            error={fileError}
            disabled={uploading}
          />
        )}
      </div>

      <div className='mt-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3'>
        {batch ? (
          <button
            type='button'
            onClick={handleBack}
            disabled={busy}
            className='border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            Cancel
          </button>
        ) : (
          <button
            type='button'
            onClick={handleDownloadTemplate}
            disabled={downloadingTemplate}
            className='border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
          >
            {downloadingTemplate ? 'Downloading...' : 'Download CSV Template'}
          </button>
        )}

        <button
          type='button'
          onClick={handleStartValidation}
          disabled={!batch || busy}
          className='bg-blue-600 text-white px-8 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed'
        >
          {uploading
            ? 'Uploading...'
            : startingValidation
            ? 'Starting...'
            : 'Validate'}
        </button>
      </div>
    </div>
  );

  const renderProgressStep = () => {
    return (
      <ImportProgress
        title={
          isProcessing ? 'Processing In Progress' : 'Validation In Progress'
        }
        subtitle={
          isProcessing
            ? 'Processing shopper payouts. This may take a few minutes.'
            : 'Validating payout data. This may take a few minutes.'
        }
        progressLabel={buildProgressLabel(batch, isProcessing)}
        ratio={shopperPayoutService.getProgressRatio(batch)}
        onCancel={isProcessing ? undefined : () => setShowLeaveModal(true)}
        cancelDisabled={busy}
      />
    );
  };

  const renderResultStep = () => (
    <>
      <div className='bg-white rounded-lg shadow-sm border border-gray-200 px-6 py-5 mb-6'>
        <h2 className='text-2xl font-bold text-gray-900'>Validation Result</h2>
        <p className='mt-1 text-sm text-gray-500'>{batch.batchCode}</p>
      </div>

      <PayoutRowsTable
        batchId={batch.id}
        summary={batch}
        showSummaryCards
        onError={handleRowsError}
        footer={
          <div className='flex items-center justify-end gap-3'>
            <button
              type='button'
              onClick={() => setShowLeaveModal(true)}
              disabled={busy}
              className='border border-gray-300 text-gray-700 px-5 py-2.5 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed'
            >
              Cancel
            </button>
            <button
              type='button'
              onClick={() => setShowConfirmModal(true)}
              disabled={busy || !batch.validRows}
              title={
                batch.validRows
                  ? undefined
                  : 'There are no valid rows to process.'
              }
              className='bg-blue-600 text-white px-6 py-2.5 rounded-md text-sm font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed'
            >
              Continue Confirm
            </button>
          </div>
        }
      />
    </>
  );

  const renderStep = () => {
    if (status === IMPORT_STATUS.VALIDATED) return renderResultStep();
    if (isPolling) return renderProgressStep();
    return renderUploadStep();
  };

  return (
    <div>
      {/* Figma drops the Back button on Validation Result; the footer Cancel
          is the way out of that step. */}
      {!isValidationResult && (
        <div className='mb-6'>
          <button
            type='button'
            onClick={handleBack}
            className='inline-flex items-center gap-2 border border-gray-300 bg-white text-gray-800 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500'
          >
            <svg
              className='w-4 h-4'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 19l-7-7m0 0l7-7m-7 7h18'
              />
            </svg>
            Back
          </button>
        </div>
      )}

      {renderErrorBanner()}
      {renderStep()}

      <ConfirmImportModal
        isOpen={showConfirmModal}
        batch={batch}
        onBack={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmImport}
        submitting={confirming}
      />

      <LeavePageModal
        isOpen={showLeaveModal}
        onLeave={handleLeavePage}
        onStay={() => setShowLeaveModal(false)}
        submitting={leaving}
      />
    </div>
  );
};

export default ShopperPayoutImportPage;
